import React, { useState, useMemo, useEffect } from 'react';
import { Button, Dropdown, message, Form, Select, Typography } from 'antd';
import { MoreOutlined, UserAddOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getData } from '../../actions/apiCall';
import { postData } from '../../actions/developer';
import FormSetup from '../Forms/FormSetup';
import { MasterConfigs } from '../../interfaces/entity';
import { constants } from '../../constants/common';
import { FormInstance } from 'antd';

const { Title } = Typography;

interface AddToBenchDeveloperProps {
    record: any;
    onSuccess?: () => void;
    open?: boolean;
    onClose?: () => void;
}

const AddToBenchDeveloper: React.FC<AddToBenchDeveloperProps> = ({ record, onSuccess, open: externalOpen, onClose: externalOnClose }) => {
    const dispatch = useDispatch();
    const [addDeveloperModal, setAddDeveloperModal] = useState(false);

    // Use external open/close if provided, otherwise use internal state
    const isModalOpen = externalOpen !== undefined ? externalOpen : addDeveloperModal;
    const handleClose = () => {
        if (externalOnClose) {
            externalOnClose();
        } else {
            setAddDeveloperModal(false);
        }
    };
    const [formInstance, setFormInstance] = useState<FormInstance | null>(null);
    const [benchDevelopers, setBenchDevelopers] = useState<any[]>([]);
    const [loadingBenchDevs, setLoadingBenchDevs] = useState(false);

    // Load bench developers to check if candidate already exists
    useEffect(() => {
        const loadBenchDevelopers = async () => {
            try {
                setLoadingBenchDevs(true);
                const response = await dispatch(getData('/bench-developers?limit=1000'));
                if (response?.data) {
                    setBenchDevelopers(response.data);
                }
            } catch (error) {
                console.error('Error loading bench developers:', error);
            } finally {
                setLoadingBenchDevs(false);
            }
        };
        loadBenchDevelopers();
    }, [dispatch]);

    // Check if candidate already exists in bench developers
    const isDeveloperInBench = (record: any) => {
        if (!record?.email) return false;
        return benchDevelopers.some(dev => dev.email?.toLowerCase() === record.email?.toLowerCase());
    };

    // Developer config for the modal form
    const developerConfig: MasterConfigs = useMemo(() => ({
        api: () => '/bench-developers',
        modalTitle: () => 'Add Developer to Bench',
        modalProps: { width: 800 },
        getFormColumns: () => [
            {
                span: 24,
                render: (form) => <Title level={5} className="font-bold pb-2">Developer Information</Title>
            },
            {
                label: 'Developer Owner',
                name: 'developerOwner',
                type: 'select',
                isCombo: true,
                rules: [{ required: true, message: 'Developer Owner is required' }],
                span: 8,
            },
            {
                label: 'First Name',
                name: 'firstName',
                type: 'text',
                inputProps: {
                    addonBefore: <Form.Item name="preName">
                        <Select style={{ width: 100 }} placeholder="Title">
                            {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                },
                rules: [{ required: true, message: 'First Name is required' }],
                span: 8,
            },
            {
                label: 'Last Name',
                name: 'lastName',
                type: 'text',
                span: 8,
            },
            {
                label: 'Email',
                name: 'email',
                type: 'text',
                span: 8,
                rules: [
                    { required: true, message: 'Email is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return emailRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid email address'));
                        },
                    },
                ],
            },
            {
                label: 'Mobile',
                name: 'mobile',
                type: 'text',
                span: 8,
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const mobileRegex = /^[0-9]{10,15}$/;
                            return mobileRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid mobile number'));
                        },
                    },
                ],
            },
            {
                label: 'Job Title',
                name: 'jobTitle',
                type: 'select',
                isCombo: true,
                span: 8,
                rules: [{ required: true, message: 'Job Title is required' }],
            },
            {
                label: 'Experience',
                name: 'experience',
                type: 'text',
                inputProps: {
                    addonAfter: 'Years',
                },
                rules: [
                    { required: true, message: 'Experience is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const experienceRegex = /^[0-9]{1,3}$/;
                            return experienceRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid experience'));
                        },
                    },
                ],
                span: 8,
            },
            {
                label: 'Developer Availability',
                name: 'developerAvailability',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    optionFilterProp: 'children',
                    allowClear: true,
                },
                children: constants.DEVELOPER_AVAILABILITY,
                rules: [{ required: true, message: 'Developer Availability is required' }],
                span: 8,
            },
            {
                label: 'Notice Period',
                name: 'noticePeriodDays',
                type: 'text',
                inputProps: {
                    addonAfter: 'Days',
                },
                span: 8,
            },
            {
                label: 'Last Working Date',
                name: 'lastWorkingDate',
                type: 'date',
                dateProps: {
                    format: 'DD/MM/YYYY',
                },
            },
            {
                label: 'Vendor Budget',
                name: 'vendorBudget',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const rangeRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
                            return rangeRegex.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid vendor billing rate'));
                        },
                    },
                ],
                span: 8,
            },
            {
                label: 'Selling Price',
                name: 'budgetRange',
                type: 'text',
                inputProps: {
                    prefix: 'INR ',
                },
                rules: [
                    { required: true, message: 'Selling Price is required' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.reject(new Error('Selling Price is required'));
                            const budgetRange = /^\s*\d+(\.\d{1, 2})?\s*-\s*\d+(\.\d{1, 2})?\s*$/;
                            return budgetRange.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('Enter a valid budget range, e.g. 10000 - 20000'));
                        },
                    },
                ],
                span: 8,
            },
            {
                label: 'City',
                name: ['address', 'city'],
                type: 'text',
                span: 8,
            },
            {
                label: 'State',
                name: ['address', 'state'],
                type: 'text',
                span: 8,
            },
            {
                label: 'Country',
                name: ['address', 'country'],
                type: 'text',
                span: 8,
            },
            {
                label: 'Zip Code',
                name: ['address', 'zip'],
                type: 'text',
                span: 8,
            },
            {
                label: 'Description',
                name: 'description',
                type: 'textarea',
                span: 8,
                textAreaProps: {
                    autoSize: { minRows: 3, maxRows: 6 }
                }
            },
        ],
        combo: [
            {
                api: '/users',
                name: 'developerOwner',
                idKey: '_id',
                render: (text) => constants.GET_FULL_NAME(text)
            },
            {
                api: '/job-titles',
                name: 'jobTitle',
                idKey: '_id',
                render: (text) => text?.name || 'N/A'
            }
        ],
        onSubmit: async (id, values) => {
            const response = await dispatch(postData('/bench-developers', values));
            if (response?.code === 200 || response?.data?._id || response?._id) {
                message.success('Developer added to bench successfully');
                handleClose();
                // Reload bench developers
                const benchResponse = await dispatch(getData('/bench-developers?limit=1000'));
                if (benchResponse?.data) {
                    setBenchDevelopers(benchResponse.data);
                }
                if (onSuccess) {
                    onSuccess();
                }
                return response;
            } else {
                message.error(response?.message || response?.data?.message || 'Failed to add developer');
                return response;
            }
        },
        onRefresh: () => {
            handleClose();
        }
    }), [dispatch, onSuccess]);

    // Pre-populate form when modal opens
    useEffect(() => {
        if (isModalOpen && formInstance && record) {
            // Convert offeredCTC to budget range format (e.g., "100000 - 120000")
            let budgetRange = '';
            let vendorBudget = '';
            if (record.offeredCTC) {
                const ctc = parseFloat(record.offeredCTC);
                if (!isNaN(ctc)) {
                    // Create a range: use offeredCTC as min, add 20% as max
                    const min = Math.round(ctc);
                    const max = Math.round(ctc * 1.2);
                    budgetRange = `${min} - ${max}`;
                    // Vendor budget: 80% of offeredCTC (vendor gets 80%, company margin is 20%)
                    vendorBudget = Math.round(ctc * 0.8).toString();
                }
            }

            const initialValues = {
                firstName: record.firstName || '',
                lastName: record.lastName || '',
                email: record.email || '',
                mobile: record.phone || '',
                jobTitle: record.profileFor?._id || record.profileFor || null,
                experience: record.experiance || 0,
                developerOwner: record.candidateOwner?._id || record.candidateOwner || null,
                noticePeriodDays: record.noticePeriod ? record.noticePeriod * 30 : 0,
                lastWorkingDate: record.lastWorkingDate || null,
                vendorBudget: vendorBudget,
                budgetRange: budgetRange,
                address: {
                    city: record.city || record.address?.city || '',
                    state: record.state || record.address?.state || '',
                    country: record.country || record.address?.country || '',
                    zip: record.zipCode || record.address?.zip || '',
                },
                description: record.description || '',
            };
            formInstance.setFieldsValue(initialValues);
        }
    }, [isModalOpen, formInstance, record]);

    const isInBench = isDeveloperInBench(record);

    if (isInBench) {
        return null;
    }

    // If external control is provided, just render the modal
    if (externalOpen !== undefined) {
        return (
            <FormSetup
                config={developerConfig}
                open={isModalOpen}
                onClose={handleClose}
                isModal={false}
                form={(form) => {
                    setFormInstance(form);
                }}
            />
        );
    }

    // Otherwise, render as dropdown button (original behavior)
    const menuItems = [
        {
            key: 'add-to-bench',
            icon: <UserAddOutlined />,
            label: 'Add to Bench',
            onClick: () => {
                setAddDeveloperModal(true);
            }
        }
    ];

    return (
        <>
            <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
            >
                <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
            <FormSetup
                config={developerConfig}
                open={isModalOpen}
                onClose={handleClose}
                isModal={false}
                form={(form) => {
                    setFormInstance(form);
                }}
            />
        </>
    );
};

export default AddToBenchDeveloper;
